
import { useEffect, useState } from 'react';
import {
  Building2,
  Palette,
  Clock,
  Brush,
  Home,
  TreePine,
  Star,
  X
} from 'lucide-react';
import { getStoredLanguage, normalizeLanguage } from '../utils/language';

const Sidebar = ({ isOpen, onClose, categories, selectedCategory, onCategorySelect }) => {
  const [currentLanguage, setCurrentLanguage] = useState(getStoredLanguage());

  useEffect(() => {
    setCurrentLanguage(getStoredLanguage());
    const handler = (e) => {
      const next = normalizeLanguage(e?.detail?.language);
      setCurrentLanguage(next);
    };
    if (typeof window !== 'undefined') window.addEventListener('app-language-change', handler);
    return () => { if (typeof window !== 'undefined') window.removeEventListener('app-language-change', handler); };
  }, []);
  const handleCategoryClick = (category) => {
    onCategorySelect(category === selectedCategory ? '' : category);
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  const getCategoryIcon = (category) => {
    const iconProps = { className: "w-5 h-5" };
    const icons = {
      'Müze': <Building2 {...iconProps} />,
      'Kültür': <Palette {...iconProps} />,
      'Tarih': <Clock {...iconProps} />,
      'Sanat': <Brush {...iconProps} />,
      'Mimari': <Home {...iconProps} />,
      'Doğa': <TreePine {...iconProps} />
    };
    return icons[category] || <Building2 {...iconProps} />;
  };

  const translateCategory = (category) => {
    const mapEn = {
      'Müzeler': 'Museums',
      'Anıtlar': 'Monuments',
      'Tarihi Mekanlar': 'Historic Sites',
      'Müze': 'Museum',
      'Kültür': 'Culture',
      'Tarih': 'History',
      'Sanat': 'Art',
      'Mimari': 'Architecture',
      'Doğa': 'Nature'
    };
    const mapRu = {
      'Müzeler': 'Музеи',
      'Anıtlar': 'Памятники',
      'Tarihi Mekanlar': 'Исторические места',
      'Müze': 'Музей',
      'Kültür': 'Культура',
      'Tarih': 'История',
      'Sanat': 'Искусство',
      'Mimari': 'Архитектура',
      'Doğa': 'Природа'
    };
    if (currentLanguage === 'EN') return mapEn[category] || category;
    if (currentLanguage === 'RU') return mapRu[category] || mapEn[category] || category;
    return category;
  };

  const t = (tr, en, ru) => {
    if (currentLanguage === 'EN') return en;
    if (currentLanguage === 'RU') return ru || en || tr;
    return tr;
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-50 w-72 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:shadow-lg
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-primary-50 to-primary-100">
            <div>
              <h2 className="text-xl font-bold text-primary-800">{t('KATEGORİLER', 'CATEGORIES', 'КАТЕГОРИИ')}</h2>
              <p className="text-sm text-primary-600 mt-1">{t('Keşfetmek istediğiniz alanı seçin', 'Choose what to explore', 'Выберите, что исследовать')}</p>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-full text-primary-400 hover:text-primary-600 hover:bg-primary-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Categories */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              {/* All Categories */}
              <button
                onClick={() => handleCategoryClick('')}
                className={`w-full text-left px-4 py-4 rounded-xl transition-all duration-200 flex items-center space-x-3 group ${
                  selectedCategory === ''
                    ? 'bg-primary-500 text-white shadow-lg transform scale-[1.02]'
                    : 'text-gray-700 hover:bg-gray-50 hover:shadow-md hover:transform hover:scale-[1.01]'
                }`}
              >
                <Star className="w-5 h-5" />
                <div>
                  <span className="font-semibold">{t('Tümü', 'All', 'Все')}</span>
                  <p className={`text-sm ${selectedCategory === '' ? 'text-primary-100' : 'text-gray-500'}`}>
                    {t('Tüm konumları görüntüle', 'View all locations', 'Показать все места')}
                  </p>
                </div>
              </button>

              {/* Individual Categories */}
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryClick(category)}
                  className={`w-full text-left px-4 py-4 rounded-xl transition-all duration-200 flex items-center space-x-3 group ${
                    selectedCategory === category
                      ? 'bg-primary-500 text-white shadow-lg transform scale-[1.02]'
                      : 'text-gray-700 hover:bg-gray-50 hover:shadow-md hover:transform hover:scale-[1.01]'
                  }`}
                >
                  {getCategoryIcon(category)}
                  <div>
                    <span className="font-semibold">{currentLanguage === 'TR' ? category : translateCategory(category)}</span>
                    <p className={`text-sm ${selectedCategory === category ? 'text-primary-100' : 'text-gray-500'}`}>
                      {currentLanguage === 'EN'
                        ? `${translateCategory(category)} places`
                        : currentLanguage === 'RU'
                          ? `Места в категории ${translateCategory(category)}`
                          : `${category} kategorisindeki yerler`}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-100 bg-gray-50">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <p className="font-bold text-gray-800 text-lg">Samsun Keşfet</p>
              <p className="text-sm text-gray-600 mt-1">{t('Kültürel mirasımızı keşfedin', 'Discover our cultural heritage', 'Откройте наше культурное наследие')}</p>
              <div className="mt-3 flex justify-center space-x-2">
                <div className="w-2 h-2 bg-primary-400 rounded-full"></div>
                <div className="w-2 h-2 bg-primary-300 rounded-full"></div>
                <div className="w-2 h-2 bg-primary-200 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
